import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { useStore } from "../store";
import { hapticImpact } from "../native/haptics";
import { EngineHaptics, engineHapticsAvailable } from "../native/engineHaptics";

// The ignition boot — JS mirror of remotion/src/timeline.ts (the motion source
// of truth; retune the engine there → retune it here). The native splash still
// is this overlay's unlit state rendered by Remotion, so native → web handoff
// is a seamless crossfade: dash off, throttle blips, redline slam, into the app.
//
// One rAF clock drives everything (tach fill, needle heat, rumble shake, plate
// light, bloom, gloss) so the web animation is numerically identical to the
// rendered video. The plate itself is the sacred `.brand` recipe, untouched —
// only the light on it (wrapper filter) ever animates.

// ---- the engine (mirrors timeline.ts) --------------------------------------
const outCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const inOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const outQuad = (t: number) => 1 - (1 - t) * (1 - t);
const inOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const seg = (ms: number, a: number, b: number) => Math.min(1, Math.max(0, (ms - a) / (b - a)));

const rpmAt = (ms: number): number => {
  if (ms < 200) return 0;
  if (ms < 420) return 0.52 * outCubic(seg(ms, 200, 420));
  if (ms < 720) return 0.52 + (0.14 - 0.52) * inOutQuad(seg(ms, 420, 720));
  if (ms < 940) return 0.14 + (0.82 - 0.14) * outCubic(seg(ms, 720, 940));
  if (ms < 1240) return 0.82 + (0.24 - 0.82) * inOutQuad(seg(ms, 940, 1240));
  if (ms < 1460) return 0.24 + (1 - 0.24) * outCubic(seg(ms, 1240, 1460));
  if (ms < 1760) return 1 - 0.05 * Math.abs(Math.sin((Math.PI * (ms - 1460)) / 95));
  if (ms < 2160) return 1 + (0.32 - 1) * outCubic(seg(ms, 1760, 2160));
  return 0.32 + 0.018 * Math.sin((2 * Math.PI * (ms - 2160)) / 240);
};
const rumbleAt = (ms: number) => ({
  x: Math.sin(ms * 0.0533) * 0.62 + Math.sin(ms * 0.0871 + 2.1) * 0.38,
  y: Math.sin(ms * 0.0611 + 1.1) * 0.58 + Math.sin(ms * 0.0977 + 4.2) * 0.42,
  r: Math.sin(ms * 0.0455 + 0.7) * 0.7 + Math.sin(ms * 0.074 + 2.9) * 0.3,
});
const rumbleAmp = (rpm: number) => 0.12 + 0.88 * rpm * rpm;

// multi-stop ramp with per-segment easing (interpolate-lite)
const ramp = (v: number, stops: number[], vals: number[], ease: (t: number) => number) => {
  if (v <= stops[0]) return vals[0];
  for (let i = 1; i < stops.length; i++) {
    if (v <= stops[i]) return vals[i - 1] + (vals[i] - vals[i - 1]) * ease(seg(v, stops[i - 1], stops[i]));
  }
  return vals[vals.length - 1];
};
const plateLightAt = (ms: number) => ramp(ms, [940, 1040, 1140, 1460, 1580], [0.2, 0.31, 0.22, 0.22, 1], outQuad);
const bloomAt = (ms: number) => ramp(ms, [1460, 1560, 2100], [0, 1, 0.3], outCubic);
const pulseAt = (ms: number) => 1 + 0.018 * ramp(ms, [1460, 1560, 2100], [0, 1, 0], outCubic);
const glossAt = (ms: number) => (ms <= 1650 ? 0 : ms >= 2250 ? 1 : inOutCubic(seg(ms, 1650, 2250)));

const HOLD_MS = 2450;        // ignite → exit (idle is running by then)
const HOLD_RM_MS = 700;      // reduced motion: light up, brief hold, out
const EXIT_MS = 400;
const FAILSAFE_MS = 6000;    // never trap the user if `ready` wedges
const IDLE_RPM = 0.32;

// ---- the haptic score ------------------------------------------------------
// Not three taps — an engine. A train of impact transients derived from the
// SAME rpm curve: tick cadence follows engine speed (an engine fires faster as
// it revs — ~140ms apart near idle, ~32ms at redline), so each rev BUILDS, the
// ease-off keeps a thinning palpable tremble instead of stopping dead, and the
// limiter reads as a ~300ms sustained heavy rumble — the crescendo. Strength
// escalates per rev: blip A stays light, blip B earns medium, only the send to
// redline goes heavy.
type TickStyle = "light" | "medium" | "heavy";
const hapticScore = (): { t: number; s: TickStyle }[] => {
  const score: { t: number; s: TickStyle }[] = [];
  let t = 0;
  while (t < HOLD_MS) {
    const r = rpmAt(t);
    if (r >= 0.06) {
      const s: TickStyle =
        t < 720 ? "light"                                      // rev A: builds, stays light
        : t < 1240 ? (r >= 0.45 ? "medium" : "light")          // rev B: stronger at its top
        : r >= 0.8 ? "heavy" : r >= 0.45 ? "medium" : "light"; // the send + falloff
      score.push({ t, s });
    }
    t += Math.max(32, 140 - 112 * Math.max(r, 0.04));
  }
  return score;
};
// idle keeps trembling through the exit fade — the engine doesn't cut out
const EXIT_IDLE_TICKS_MS = [110, 240, 370] as const;

// ---- the V8 (CoreHaptics, build ≥18) ---------------------------------------
// Where the native EngineHaptics plugin exists, the transient score upgrades
// to a true continuous rumble: an intensity/sharpness-curved bed that IS the
// rpm signal (the throat), with the same firing cadence layered as transient
// pops on top (the bark). The bed runs through the exit fade so the engine
// idles out from under you. All times below are ms → converted to seconds.
const V8_BED_KEYS_MS = [0, 200, 270, 420, 570, 720, 940, 1090, 1240, 1460, 1760, 1960, 2160, 2450, 2850];
const v8Pattern = () => {
  const intensity = V8_BED_KEYS_MS.map(t => {
    const r = rpmAt(Math.min(t, HOLD_MS));
    const v = t < 210 ? 0 : 0.3 + 0.7 * Math.pow(r, 1.35);
    return [t / 1000, +v.toFixed(3)];
  });
  const sharpness = [0, 940, 1460, 1760, 2160, 2850].map(t => [
    t / 1000,
    +(0.25 + 0.4 * rpmAt(Math.min(t, HOLD_MS))).toFixed(3),
  ]);
  const transients = hapticScore().map(({ t, s }) => [
    t / 1000,
    s === "heavy" ? 1 : s === "medium" ? 0.7 : 0.45,
    s === "heavy" ? 0.7 : s === "medium" ? 0.55 : 0.4,
  ]);
  return { duration: 2.9, intensity, sharpness, transients };
};

// ---- geometry (mirrors timeline.ts GEO, in a 100-unit viewBox) -------------
const TICKS = 56;
const ARC_START = 135;
const ARC_SWEEP = 270;
const MAJOR_EVERY = 7;
const VB = 100;
const R = VB / 2;
const MAX_LEN = (0.027 / 0.92) * VB;
const ticks = Array.from({ length: TICKS }, (_, i) => {
  const p = i / (TICKS - 1);
  const a = ((ARC_START + ARC_SWEEP * p) * Math.PI) / 180;
  const major = i % MAJOR_EVERY === 0;
  const len = ((major ? 0.027 : 0.016) / 0.92) * VB;
  const w = ((major ? 0.005 : 0.0035) / 0.92) * VB;
  const r0 = R - len;
  return { x1: R + Math.cos(a) * r0, y1: R + Math.sin(a) * r0, x2: R + Math.cos(a) * R, y2: R + Math.sin(a) * R, w };
});
// the tach arc the mask + hot-zone strokes follow (135° → 405°, clockwise)
const rc = R - MAX_LEN / 2;
const pt = (deg: number) => `${R + Math.cos((deg * Math.PI) / 180) * rc} ${R + Math.sin((deg * Math.PI) / 180) * rc}`;
const ARC_D = `M ${pt(ARC_START)} A ${rc} ${rc} 0 1 1 ${pt(ARC_START + ARC_SWEEP)}`;

const BOOT_SESSION_KEY = "y3lo.boot.v1";

// Native cold starts always boot. On web, boot only for returning app users
// (token in Preferences' localStorage mirror), once per browser session —
// anonymous visitors go straight to the marketing Landing.
const shouldBoot = () => {
  if (Capacitor.isNativePlatform()) return true;
  try {
    if (sessionStorage.getItem(BOOT_SESSION_KEY)) return false;
    return !!localStorage.getItem("CapacitorStorage.yelo.token.v1");
  } catch {
    return false;
  }
};

const styles = `
.bs {
  /* explicit viewport sizing, not inset:0 — Ionic transforms <body>
     (translateZ(0) in structure.css), making it the containing block for
     fixed descendants; if body is ever content-height (Landing's scroll
     unlock), inset-based sizing would stretch the overlay to the page */
  position: fixed; top: 0; left: 0;
  width: 100vw; height: 100vh; height: 100dvh;
  z-index: var(--z-boot, 1500);
  background: var(--yelo-ink, #0E0F11);
  display: flex; align-items: center; justify-content: center;
  --bs-u: min(100vw, 480px);
}
.bs-exit { animation: bsOut ${EXIT_MS}ms ease forwards; }
@keyframes bsOut { to { opacity: 0 } }

.bs-backlight {
  position: absolute; inset: 0; opacity: .2; pointer-events: none;
  background:
    radial-gradient(120% 90% at 8% 0%, rgba(255,212,0,.1), transparent 55%),
    radial-gradient(120% 90% at 92% 100%, rgba(255,212,0,.075), transparent 55%),
    radial-gradient(90% 70% at 50% 46%, rgba(255,212,0,.065), transparent 62%);
}

.bs-lock {
  position: relative;
  width: calc(var(--bs-u) * .92); height: calc(var(--bs-u) * .92);
  transform: translateY(-4%);
  will-change: transform;
}
.bs-lock svg { position: absolute; inset: 0; overflow: visible; width: 100%; height: 100%; }
.bs-dim line { stroke: #F5F5F2; opacity: .055; }
.bs-lit line { stroke: #FFD400; opacity: .52; }
.bs-hot { filter: blur(3px); }

.bs-plate-seat { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.bs-plate {
  position: relative;
  font-size: calc(var(--bs-u) * .15);
  border-radius: .2em;
  filter: brightness(.2) saturate(.55);
  will-change: filter, box-shadow;
}
.bs-plate .brand { font-size: 1em; }
.bs-gloss {
  position: absolute; inset: 4%; border-radius: .16em;
  overflow: hidden; pointer-events: none;
}
.bs-gloss i {
  position: absolute; top: -40%; bottom: -40%; left: 0; width: 34%;
  transform: translateX(-140%) skewX(-22deg);
  background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.42) 50%, rgba(255,255,255,0));
}

/* reduced motion: no revs, no rumble — the dash simply comes on */
@media (prefers-reduced-motion: reduce) {
  .bs-rm-go .bs-plate { animation: bsPlateRM 480ms ease both; }
  .bs-rm-go .bs-backlight { opacity: .38; transition: opacity 480ms ease; }
}
@keyframes bsPlateRM { to { filter: brightness(1) saturate(1); } }
`;

export const BootSplash = () => {
  const [phase, setPhase] = useState<"unlit" | "go" | "exit" | "done">(() =>
    shouldBoot() ? "unlit" : "done",
  );
  const ready = useStore(s => s.ready);
  const lockRef = useRef<HTMLDivElement | null>(null);
  const backRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<SVGPathElement | null>(null);
  const hotRef = useRef<SVGPathElement | null>(null);
  const plateRef = useRef<HTMLDivElement | null>(null);
  const glossRef = useRef<HTMLElement | null>(null);
  const engineOkRef = useRef(false);

  // silence the V8 if the overlay dies for any reason (unmount, view-leave)
  useEffect(() => () => {
    if (engineOkRef.current) EngineHaptics.stop().catch(() => {});
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(BOOT_SESSION_KEY, "1"); } catch { /* private mode */ }
  }, []);

  // key turn: ignite shortly after hydrate so the native splash's 250ms fade
  // has mostly cleared before the first blip
  useEffect(() => {
    if (phase !== "unlit" || !ready) return;
    const t = window.setTimeout(() => setPhase("go"), 120);
    return () => clearTimeout(t);
  }, [ready, phase]);

  // the engine: one rAF clock drives tach, rumble, plate light, bloom, gloss
  useEffect(() => {
    if (phase !== "go") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      // static "engine on": idle tach, lit plate (CSS handles the crossfade)
      maskRef.current?.setAttribute("stroke-dashoffset", String(1 - IDLE_RPM));
      const t = window.setTimeout(() => setPhase("exit"), HOLD_RM_MS);
      return () => clearTimeout(t);
    }

    // the engine in the hand. Preferred: the CoreHaptics V8 (build ≥18) — a
    // continuous curved rumble bed + firing pops, one native call. Fallback
    // (older binaries via OTA, Android, or engine rejection on non-Taptic
    // hardware): the transient score fired off the same clock as the visuals.
    // Ticks older than 90ms are dropped, so a stalled frame can't dump a
    // burst of stale impacts at once.
    if (engineHapticsAvailable()) {
      engineOkRef.current = true;
      EngineHaptics.play(v8Pattern()).catch(() => { engineOkRef.current = false; });
    }
    const score = hapticScore();
    let si = 0;

    const t0 = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const ms = now - t0;
      const rpm = rpmAt(ms);

      while (si < score.length && score[si].t <= ms) {
        if (!engineOkRef.current && score[si].t > ms - 90) hapticImpact(score[si].s);
        si++;
      }

      maskRef.current?.setAttribute("stroke-dashoffset", String(1 - rpm));
      if (hotRef.current) {
        hotRef.current.setAttribute("stroke-dashoffset", String(0.07 - rpm));
        hotRef.current.setAttribute("opacity", rpm > 0.015 ? "0.55" : "0");
      }

      const lock = lockRef.current;
      if (lock) {
        const n = rumbleAt(ms);
        const amp = rumbleAmp(rpm);
        const px = amp * 0.0038 * Math.min(window.innerWidth, 480);
        lock.style.transform =
          `translateY(-4%) translate(${(n.x * px).toFixed(2)}px, ${(n.y * px).toFixed(2)}px) rotate(${(n.r * 0.14 * amp).toFixed(3)}deg)`;
      }
      if (backRef.current) backRef.current.style.opacity = String(0.22 + 0.2 * rpm);

      const plate = plateRef.current;
      if (plate) {
        const light = plateLightAt(ms);
        const desat = 0.55 + (1 - 0.55) * ((light - 0.2) / 0.8);
        plate.style.filter = `brightness(${light.toFixed(3)}) saturate(${desat.toFixed(3)})`;
        const bloom = bloomAt(ms);
        const aura = bloom * 0.55;
        plate.style.boxShadow = aura > 0.01 ? `0 0 ${(1.6 * aura).toFixed(2)}em ${(0.12 * aura).toFixed(2)}em rgba(255,212,0,${(0.55 * aura).toFixed(2)})` : "none";
        plate.style.transform = `scale(${pulseAt(ms).toFixed(4)})`;
      }
      if (glossRef.current) {
        const g = glossAt(ms);
        glossRef.current.style.transform = `translateX(${(-140 + g * 540).toFixed(1)}%) skewX(-22deg)`;
      }

      if (ms < HOLD_MS) raf = requestAnimationFrame(frame);
      else setPhase("exit");
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (phase !== "exit") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // the V8 bed already idles through the exit; the tick tail is score-only
    const idle = reduce || engineOkRef.current
      ? []
      : EXIT_IDLE_TICKS_MS.map(t => window.setTimeout(() => hapticImpact("light"), t));
    const t = window.setTimeout(() => setPhase("done"), EXIT_MS);
    return () => { clearTimeout(t); idle.forEach(clearTimeout); };
  }, [phase]);

  useEffect(() => {
    const t = window.setTimeout(() => setPhase(p => (p === "unlit" ? "exit" : p)), FAILSAFE_MS);
    return () => clearTimeout(t);
  }, []);

  if (phase === "done") return null;

  const going = phase === "go" || phase === "exit";
  return createPortal(
    <div className={`bs${going ? " bs-rm-go" : ""}${phase === "exit" ? " bs-exit" : ""}`} aria-hidden>
      <style>{styles}</style>
      <div ref={backRef} className="bs-backlight" />
      <div ref={lockRef} className="bs-lock">
        <svg viewBox={`0 0 ${VB} ${VB}`}>
          <defs>
            <mask id="bs-tach">
              <rect x="-10" y="-10" width={VB + 20} height={VB + 20} fill="black" />
              <path
                ref={maskRef}
                d={ARC_D}
                fill="none"
                stroke="white"
                strokeWidth={MAX_LEN * 1.6}
                strokeLinecap="round"
                pathLength={1}
                strokeDasharray="1 1"
                strokeDashoffset={1}
              />
            </mask>
          </defs>
          <g className="bs-dim">
            {ticks.map((t, i) => (
              <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} strokeWidth={t.w} strokeLinecap="round" />
            ))}
          </g>
          <g className="bs-lit" mask="url(#bs-tach)">
            {ticks.map((t, i) => (
              <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} strokeWidth={t.w} strokeLinecap="round" />
            ))}
          </g>
          {/* the needle's hot zone — a glowing 7% of arc trailing the head */}
          <path
            ref={hotRef}
            className="bs-hot"
            d={ARC_D}
            fill="none"
            stroke="#FFD400"
            strokeWidth={MAX_LEN * 1.2}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray="0.07 0.93"
            strokeDashoffset={0.07}
            opacity={0}
          />
        </svg>
        <div className="bs-plate-seat">
          <div ref={plateRef} className="bs-plate">
            <div className="brand">Y3LO</div>
            <div className="bs-gloss"><i ref={glossRef as any} /></div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
